"use client";

import React from "react";
import { Plane, Compass, Briefcase, Clock, ShieldCheck, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
export default function ServicesShowcase() {
  const shouldReduceMotion = useReducedMotion();
  const services = [{
    title: "Commercial Airport Transfers",
    subtitle: "ORD • MDW",
    description: "Direct curbside or baggage meet-and-greet timed precisely to real-time flight arrivals.",
    icon: Plane,
    badge: "Flight Monitored",
    detail: "O'Hare & Midway terminals"
  }, {
    title: "Private Aviation & FBO Links",
    subtitle: "Signature • Atlantic • DuPage",
    description: "Coordinated ground transport syncing directly with general aviation pilots and tarmac arrivals.",
    icon: Compass,
    badge: "FBO Coordinated",
    detail: "All regional executive airfields"
  }, {
    title: "Corporate & Executive Travel",
    subtitle: "Loop • Suburbs • Interstate",
    description: "Discreet sedans and SUVs equipped for productive travel, roadshows, and executive teams.",
    icon: Briefcase,
    badge: "Corporate Accounts",
    detail: "Point-to-point flat rates"
  }, {
    title: "Dedicated Hourly Charters",
    subtitle: "Flexible As-Directed",
    description: "Your chauffeur and vehicle on standby for multi-stop meetings, dinner galas, and events.",
    icon: Clock,
    badge: "Zero Surge Pricing",
    detail: "Continuous standby service"
  }];
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="servicesshowcase" data-nav="dark" className="dark bg-background text-foreground relative py-20 md:py-28 overflow-hidden border-t border-border/40">
      {/* Background Ambient Radial Glow */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_15%,hsl(42_65%_54%/0.08),transparent_65%)]" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-7xl">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }} className="text-center max-w-3xl mx-auto mb-14 md:mb-18">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-primary/10 border border-primary/25 text-primary text-xs font-semibold tracking-wider uppercase mb-4">
            <ShieldCheck className="size-3.5" />
            <span>Charter & Airport Services</span>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-5 leading-tight">
            Ground travel defined by{" "}
            <span className="font-serif italic text-primary font-normal">precision</span> and composure
          </h2>

          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
            From scheduled O'Hare and Midway tarmac pickups to interstate executive travel, enjoy absolute flat-rate transparency with zero surge charges.
          </p>
        </motion.div>

        {/* 4-Card Service Matrix */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 mb-16">
          {services.map((service, index) => {
          const Icon = service.icon;
          return <motion.div key={service.title} variants={itemVariants} data-index={index}>
                <Card className="h-full bg-card/85 backdrop-blur-xs border border-border/80 rounded-xl relative overflow-hidden transition-[border-color,box-shadow] duration-300 hover:border-primary/50 hover:shadow-lg group flex flex-col justify-between p-0">
                  {/* Executive Top Hairline Metallic Accent */}
                  <div aria-hidden="true" className="absolute top-0 inset-x-0 h-0.5 bg-gradient-to-r from-primary/60 via-primary/30 to-transparent group-hover:from-primary group-hover:to-primary/60 transition-colors duration-300" />

                  <CardContent className="p-6 sm:p-7 flex flex-col flex-1">
                    {/* Header Row: Icon & Tag */}
                    <div className="flex items-start justify-between gap-3 mb-5">
                      <div className="p-3 rounded-lg bg-background/90 border border-border/60 text-primary transition-[border-color,background-color] duration-250 group-hover:border-primary/40 group-hover:bg-accent/40">
                        <Icon className="size-5 sm:size-6 stroke-[1.75]" />
                      </div>
                      <span className="text-[11px] font-mono font-medium px-2.5 py-1 rounded-lg bg-secondary text-secondary-foreground border border-border/60">
                        {service.badge}
                      </span>
                    </div>

                    <h3 className="text-lg font-semibold text-foreground mb-1 leading-snug">
                      {service.title}
                    </h3>
                    <p className="text-xs font-mono text-primary/90 mb-3 tracking-wide">
                      {service.subtitle}
                    </p>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                      {service.description}
                    </p>

                    <div className="mt-auto pt-4 border-t border-border/50 flex items-center gap-2 text-xs text-muted-foreground">
                      <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                      <span>{service.detail}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Fleet Tier Presentation */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.55,
        ease: [0.16, 1, 0.3, 1],
        delay: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-14">
          {/* Executive Sedan Card */}
          <div className="group relative rounded-xl overflow-hidden border border-border/80 bg-card shadow-md flex flex-col justify-between">
            <div className="relative aspect-16/10 w-full overflow-hidden bg-background">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/luxury_black_sedan_chicagoland.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/luxury_black_sedan_chicagoland.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/luxury_black_sedan_chicagoland.webp-wvc-sizes" alt="Executive black luxury sedan curbside at Chicago private terminal" loading="lazy" decoding="async" className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-103" />
              <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-card via-card/40 to-transparent opacity-90" />
              <div className="absolute top-4 left-4">
                <span className="text-xs font-mono font-medium tracking-wide uppercase px-3 py-1 rounded-lg bg-background/80 text-foreground backdrop-blur-xs border border-border/60">
                  Individual & Duo Travel
                </span>
              </div>
            </div>

            <div className="p-6 sm:p-7 border-t border-border/50 flex flex-col">
              <div className="flex items-baseline justify-between mb-2">
                <h4 className="text-xl sm:text-2xl font-bold text-foreground">
                  Executive Black Sedan
                </h4>
                <span className="text-xs font-mono text-primary font-medium">1–3 Passengers</span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                Acoustically insulated luxury cabin with rear climate controls, bottled refreshments, and space for up to 3 checked bags.
              </p>
              <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-border/40 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="size-3.5 text-primary" />
                  Direct terminal curbside
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="size-3.5 text-primary" />
                  Complimentary flight delay buffer
                </span>
              </div>
            </div>
          </div>

          {/* Executive SUV Card */}
          <div className="group relative rounded-xl overflow-hidden border border-border/80 bg-card shadow-md flex flex-col justify-between">
            <div className="relative aspect-16/10 w-full overflow-hidden bg-background">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/executive_black_suv_service.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/executive_black_suv_service.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/executive_black_suv_service.png-wvc-sizes" alt="Executive black luxury SUV parked in Chicago evening light" loading="lazy" decoding="async" className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-103" />
              <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-card via-card/40 to-transparent opacity-90" />
              <div className="absolute top-4 left-4">
                <span className="text-xs font-mono font-medium tracking-wide uppercase px-3 py-1 rounded-lg bg-background/80 text-foreground backdrop-blur-xs border border-border/60">
                  Leadership Teams & Groups
                </span>
              </div>
            </div>

            <div className="p-6 sm:p-7 border-t border-border/50 flex flex-col">
              <div className="flex items-baseline justify-between mb-2">
                <h4 className="text-xl sm:text-2xl font-bold text-foreground">
                  Premium Executive SUV
                </h4>
                <span className="text-xs font-mono text-primary font-medium">Up to 6 Passengers</span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                Full-size extended passenger volume with high luggage capacity for golf travel, executive delegations, and family airport transfers.
              </p>
              <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-border/40 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="size-3.5 text-primary" />
                  6 Full-size luggage capacity
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="size-3.5 text-primary" />
                  All-weather executive AWD
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Bottom CTA Block */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1],
        delay: 0.15
      }} className="flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
          <Button asChild size="lg" className="w-full sm:w-auto px-8 py-6 text-base font-semibold shadow-md transition-[color,background-color,border-color,box-shadow]">
            <Link to="/OrderPage" className="inline-flex items-center justify-center gap-2">
              Book Your Ride
              <ArrowRight className="size-4" />
            </Link>
          </Button>

          <Button asChild variant="outline" size="lg" className="w-full sm:w-auto px-7 py-6 text-base font-semibold border-border/80 hover:border-primary/50 transition-[color,background-color,border-color,box-shadow]">
            <Link to="/AboutUsPage">
              Explore Fleet Standards
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>;
}